import React, { useRef, useEffect } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import RevealSection from '../ui/RevealSection';

gsap.registerPlugin(ScrollTrigger);

const teamAreas = [
  {
    title: 'Técnicos',
    description:
      'Ingenieros de IA, especialistas en ciberseguridad, arquitectos de sistemas e infraestructura crítica. Diseñamos, construimos y sostenemos las soluciones que operan en entornos de alta exigencia.',
  },
  {
    title: 'Funcionales',
    description:
      'Estrategas, operadores logísticos, gestores energéticos y consultores de negocio. Traducimos la complejidad técnica en decisiones, procesos y resultados medibles para el cliente.',
  },
];

export default function Team() {
  const sectionRef = useRef(null);

  useEffect(() => {
    const rows = sectionRef.current?.querySelectorAll('.team-row');
    if (!rows) return;

    rows.forEach((row) => {
      const num = row.querySelector('.team-num');
      const title = row.querySelector('.team-title');
      const desc = row.querySelector('.team-desc');

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: row,
          start: 'top 80%',
          toggleActions: 'play none none none',
        },
      });

      tl.fromTo(num, { opacity: 0, x: -30 }, { opacity: 1, x: 0, duration: 0.6, ease: 'power3.out' })
        .fromTo(title, { opacity: 0, y: 20 }, { opacity: 1, y: 0, duration: 0.6, ease: 'power3.out' }, '-=0.4')
        .fromTo(desc, { opacity: 0, y: 15 }, { opacity: 1, y: 0, duration: 0.5, ease: 'power3.out' }, '-=0.3');
    });

    return () => ScrollTrigger.getAll().forEach((st) => st.kill());
  }, []);

  return (
    <section id="team" ref={sectionRef} className="section py-32">
      <div className="max-w-6xl mx-auto w-full">
        <div className="mb-20 text-center">
          <RevealSection>
            <p className="text-white/40 text-sm font-body font-extralight uppercase tracking-[0.3em] mb-6">
              El Equipo
            </p>
          </RevealSection>
        </div>

        <div className="space-y-0 mt-16">
          {teamAreas.map((area, i) => {
            const isEven = i % 2 === 0;

            return (
              <div
                key={area.title}
                className="team-row group relative py-12 md:py-16 border-t border-white/[0.06] last:border-b hover:bg-white/[0.02] transition-colors duration-700"
              >
                <div className={`flex flex-col md:flex-row items-start gap-6 md:gap-0 ${isEven ? '' : 'md:flex-row-reverse'}`}>
                  {/* Number - oversized, semi-transparent */}
                  <div className={`md:w-1/6 flex ${isEven ? 'md:justify-start' : 'md:justify-end'}`}>
                    <span className="team-num font-display font-thin text-6xl md:text-8xl leading-none text-white/[0.04] group-hover:text-white/[0.10] transition-colors duration-700 select-none">
                      {String(i + 1).padStart(2, '0')}
                    </span>
                  </div>

                  {/* Title */}
                  <div className={`md:w-2/6 ${isEven ? 'md:pl-4' : 'md:pr-4 md:text-right'}`}>
                    <h3 className="team-title font-display font-medium text-3xl md:text-4xl text-white/70 group-hover:text-white transition-colors duration-500 leading-tight">
                      {area.title}
                    </h3>
                  </div>

                  {/* Description */}
                  <div className={`md:w-3/6 ${isEven ? 'md:pl-8' : 'md:pr-8 md:text-right'}`}>
                    <p className="team-desc font-body font-extralight text-sm md:text-base leading-relaxed text-white/25 group-hover:text-white/50 transition-colors duration-500 max-w-lg">
                      {area.description}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
